import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { type ComponentProps, useId, useRef, useState } from 'react'
import { cn } from '../lib/cn'
import { controlClassName } from '../lib/control-styles'
import { FieldContext, useFieldControlProps } from '../lib/field-context'
import { Button } from './button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog'

function dateValue(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function parseDate(value: string) {
  const date = new Date(`${value}T12:00:00`)
  return !Number.isNaN(date.getTime()) && dateValue(date) === value ? date : undefined
}

type DatePickerProps = Omit<
  ComponentProps<'button'>,
  'value' | 'defaultValue' | 'onChange' | 'children' | 'type'
> & {
  value: string
  onValueChange: (value: string) => void
  min?: string
  readOnly?: boolean
  labels: {
    placeholder: string
    title: string
    close: string
    previousMonth: string
    nextMonth: string
    today: string
    clear: string
    weekdays: string[]
  }
  formatDate: (date: Date) => string
  formatMonth: (date: Date) => string
}

export function DatePicker({
  value,
  onValueChange,
  labels,
  formatDate,
  formatMonth,
  readOnly,
  disabled,
  min,
  name,
  className,
  onClick,
  ...props
}: DatePickerProps) {
  const field = useFieldControlProps(props)
  const [open, setOpen] = useState(false)
  const dayRef = useRef<HTMLButtonElement>(null)
  const monthId = useId()
  const selected = parseDate(value)
  const minimum = min ? parseDate(min) : undefined
  const today = dateValue(new Date())
  const initialFocusedDate = () => {
    const candidate = selected ?? new Date()
    return minimum && candidate < minimum ? minimum : candidate
  }
  const [focused, setFocused] = useState(initialFocusedDate)
  const first = new Date(focused.getFullYear(), focused.getMonth(), 1, 12)
  const offset = (first.getDay() + 6) % 7
  const days = new Date(focused.getFullYear(), focused.getMonth() + 1, 0).getDate()
  const weekCount = Math.ceil((offset + days) / 7)
  const previousMonthUnavailable = minimum
    ? new Date(focused.getFullYear(), focused.getMonth(), 0, 12) < minimum
    : false

  function choose(date: string) {
    if (date && minimum && date < dateValue(minimum)) return
    onValueChange(date)
    setOpen(false)
  }

  function focusDate(date: Date) {
    setFocused(minimum && date < minimum ? minimum : date)
  }

  function moveMonth(amount: number) {
    const next = new Date(focused.getFullYear(), focused.getMonth() + amount, 1, 12)
    next.setDate(
      Math.min(focused.getDate(), new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()),
    )
    focusDate(next)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next && (disabled || readOnly)) return
        if (next) focusDate(initialFocusedDate())
        setOpen(next)
      }}
    >
      {name ? <input type="hidden" name={name} value={value} disabled={disabled} /> : null}
      <DialogTrigger asChild>
        <button
          {...props}
          {...field}
          type="button"
          disabled={disabled}
          aria-disabled={readOnly}
          onClick={onClick}
          data-slot="date-picker"
          className={controlClassName(
            'outline',
            cn('flex items-center justify-between gap-3 text-left', className),
          )}
        >
          <span className={selected ? undefined : 'text-muted-foreground'}>
            {selected ? formatDate(selected) : labels.placeholder}
          </span>
          <CalendarDaysIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      </DialogTrigger>
      <FieldContext.Provider value={null}>
        <DialogContent
          width="compact"
          className="max-w-[23rem]"
          closeLabel={labels.close}
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            dayRef.current?.focus()
          }}
        >
          <DialogHeader>
            <DialogTitle>{labels.title}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3 px-4">
            <div className="flex items-center justify-between gap-2">
              <Button
                appearance="ghost"
                tone="neutral"
                size="icon"
                aria-label={labels.previousMonth}
                disabled={previousMonthUnavailable}
                onClick={() => moveMonth(-1)}
              >
                <ChevronLeftIcon className="size-4" aria-hidden="true" />
              </Button>
              <span id={monthId} aria-live="polite" className="text-sm font-semibold">
                {formatMonth(focused)}
              </span>
              <Button
                appearance="ghost"
                tone="neutral"
                size="icon"
                aria-label={labels.nextMonth}
                onClick={() => moveMonth(1)}
              >
                <ChevronRightIcon className="size-4" aria-hidden="true" />
              </Button>
            </div>
            <table
              // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Interactive calendar grid uses roving focus on day buttons.
              role="grid"
              aria-labelledby={monthId}
              className="w-full table-fixed border-separate border-spacing-1"
            >
              <thead>
                <tr>
                  {labels.weekdays.map((day) => (
                    <th
                      key={day}
                      className="pb-2 text-center text-xs font-medium text-muted-foreground"
                    >
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: weekCount }, (_, week) => week * 7 - offset + 1).map(
                  (startDay) => (
                    <tr key={startDay}>
                      {Array.from({ length: 7 }, (_, column) => {
                        const day = startDay + column
                        if (day < 1 || day > days) return <td key={day} />
                        const date = new Date(focused.getFullYear(), focused.getMonth(), day, 12)
                        const iso = dateValue(date)
                        const unavailable = minimum ? date < minimum : false
                        const active = day === focused.getDate()
                        return (
                          <td
                            key={iso}
                            tabIndex={-1}
                            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Calendar grid cells contain focusable day buttons.
                            role="gridcell"
                            aria-selected={iso === value}
                          >
                            <Button
                              ref={active ? dayRef : undefined}
                              tabIndex={active ? 0 : -1}
                              disabled={unavailable}
                              appearance={iso === value ? 'solid' : 'ghost'}
                              tone={iso === value ? 'accent' : 'neutral'}
                              className={cn(
                                'h-10 min-h-10 w-full p-0',
                                iso === today && 'ring-1 ring-action/40',
                              )}
                              aria-label={formatDate(date)}
                              aria-current={iso === today ? 'date' : undefined}
                              onClick={() => choose(iso)}
                              onKeyDown={(event) => {
                                const delta = {
                                  ArrowLeft: -1,
                                  ArrowRight: 1,
                                  ArrowUp: -7,
                                  ArrowDown: 7,
                                  Home: -column,
                                  End: 6 - column,
                                }[event.key]
                                if (delta !== undefined) {
                                  event.preventDefault()
                                  const next = new Date(date)
                                  next.setDate(day + delta)
                                  focusDate(next)
                                } else if (event.key === 'PageUp' || event.key === 'PageDown') {
                                  event.preventDefault()
                                  moveMonth(event.key === 'PageUp' ? -1 : 1)
                                } else return
                                requestAnimationFrame(() => dayRef.current?.focus())
                              }}
                            >
                              {day}
                            </Button>
                          </td>
                        )
                      })}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </DialogBody>
          <DialogFooter className="flex justify-between gap-2">
            <Button appearance="ghost" tone="neutral" onClick={() => choose('')}>
              {labels.clear}
            </Button>
            <Button
              appearance="outline"
              disabled={minimum ? today < dateValue(minimum) : false}
              onClick={() => choose(today)}
            >
              {labels.today}
            </Button>
          </DialogFooter>
        </DialogContent>
      </FieldContext.Provider>
    </Dialog>
  )
}
